import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, NotebookPen, Search, Lock } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AdminNotes() {
  const [notes, setNotes] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [text, setText] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin-notes");
      setNotes(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return notes;
    return notes.filter((n) => n.text.toLowerCase().includes(s));
  }, [notes, q]);

  const openNew = () => { setEditing(null); setText(""); setOpenForm(true); };
  const openEdit = (n) => { setEditing(n); setText(n.text); setOpenForm(true); };

  const save = async () => {
    const t = text.trim();
    if (!t) return toast.error("Note text required");
    try {
      if (editing) {
        await api.patch(`/admin-notes/${editing.id}`, { text: t });
        toast.success("Note updated");
      } else {
        await api.post("/admin-notes", { text: t });
        toast.success("Note saved");
      }
      setOpenForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin-notes/${confirmDel.id}`);
      toast.success("Note deleted");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Private Notes" subtitle="Your personal notepad — visible only to you" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <Card className="p-5 rounded-xl border-gray-200 shadow-sm bg-white">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Personal notebook</div>
                  <Lock className="h-3 w-3 text-gray-400" />
                </div>
                <div className="font-display text-xl font-bold text-gray-900 mt-0.5">{notes.length} note{notes.length === 1 ? "" : "s"}</div>
                <div className="text-[11px] text-gray-500 mt-1">Not visible to Client, Co-ordinator, Promoter, or other Admins.</div>
              </div>
              <Button data-testid="new-admin-note-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721] text-white">
                <Plus className="h-4 w-4 mr-1.5" /> New note
              </Button>
            </div>
            <div className="mt-4 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input data-testid="admin-notes-search" placeholder="Search your notes…"
                value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
            </div>
          </Card>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <Card className="p-10 rounded-xl border-dashed border-gray-300 text-center bg-white">
              <NotebookPen className="h-8 w-8 mx-auto text-gray-400" />
              <div className="mt-2 text-sm text-gray-500">
                {q ? "No matches." : "No notes yet. Jot down a quick thought."}
              </div>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="admin-notes-list">
              {filtered.map((n) => (
                <Card key={n.id} className="p-4 rounded-xl border-gray-200 bg-white" data-testid={`admin-note-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-gray-500 mb-1">{fmtDate(n.created_at)}{n.updated_at ? ` · edited ${fmtDate(n.updated_at)}` : ""}</div>
                      <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">{n.text}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEdit(n)} title="Edit"
                        className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600"
                        data-testid={`edit-admin-note-${n.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setConfirmDel(n)} title="Delete"
                        className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-600"
                        data-testid={`del-admin-note-${n.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="admin-note-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit note" : "New private note"}</DialogTitle></DialogHeader>
          <div>
            <Textarea data-testid="admin-note-text" rows={6}
              placeholder="Anything for your own reference…"
              value={text} onChange={(e) => setText(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>Cancel</Button>
            <Button data-testid="save-admin-note-btn" onClick={save} className="bg-brand-red hover:bg-[#B31721] text-white">
              {editing ? "Save" : "Save note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent data-testid="admin-note-del-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete note?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">This can't be undone.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-del-admin-note" onClick={del} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
